/**
 * Système d'unités du noyau (CLAUDE.md §1, SPEC.md §3.1).
 *
 * Toute grandeur physique est une `Quantity` : une valeur *et* son unité.
 * Aucun nombre nu ne circule — la règle de lint `verlet/no-bare-number`
 * interdit le type `number` partout dans `src/core/**` sauf ici et dans
 * `math.ts`, les deux fichiers qui fondent le système.
 *
 * Deux niveaux de vérification, et il faut les deux :
 *
 * - **à la compilation**, `Quantity<D>` est paramétré par sa dimension, donc
 *   additionner une longueur et un temps ne compile pas ;
 * - **à l'exécution**, chaque opération revérifie, parce que les données
 *   chargées depuis un CSV ne passent jamais par le compilateur.
 *
 * Le stockage interne est en SI canonique ; la conversion n'a lieu qu'aux
 * frontières — saisie et affichage.
 */

import { coreFr } from "../i18n/core-fr.js";
import { PI } from "./math.js";

export type Dimension =
  | "length"
  | "angle"
  | "time"
  | "mass"
  | "velocity"
  | "acceleration"
  | "dimensionless";

interface UnitsByDimension {
  length: "m" | "km" | "cm" | "mm";
  angle: "rad" | "deg";
  time: "s" | "min" | "h" | "d";
  mass: "kg" | "g" | "t";
  velocity: "m/s" | "km/s" | "km/h";
  acceleration: "m/s2";
  dimensionless: "1";
}

/** Les unités valides pour une dimension donnée. */
export type UnitOf<D extends Dimension> = UnitsByDimension[D];

/** Vocabulaire fermé. Une unité absente d'ici n'existe pas pour le noyau. */
export type Unit = UnitOf<Dimension>;

type DimensionOfUnit<U extends Unit> = U extends UnitsByDimension["length"]
  ? "length"
  : U extends UnitsByDimension["angle"]
    ? "angle"
    : U extends UnitsByDimension["time"]
      ? "time"
      : U extends UnitsByDimension["mass"]
        ? "mass"
        : U extends UnitsByDimension["velocity"]
          ? "velocity"
          : U extends UnitsByDimension["acceleration"]
            ? "acceleration"
            : "dimensionless";

/**
 * Une grandeur physique. Immuable : deux mesures qui partagent une `Quantity`
 * ne peuvent pas se la modifier mutuellement.
 */
export interface Quantity<D extends Dimension = Dimension> {
  readonly value: number;
  readonly unit: UnitOf<D>;
}

/** Levée dès qu'une opération violerait la cohérence dimensionnelle. */
export class UnitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnitError";
  }
}

interface UnitSpec {
  readonly dimension: Dimension;
  /** Combien d'unités canoniques vaut 1 unité de celle-ci. */
  readonly canonicalPerUnit: number;
}

const SPECS = {
  m: { dimension: "length", canonicalPerUnit: 1 },
  km: { dimension: "length", canonicalPerUnit: 1000 },
  cm: { dimension: "length", canonicalPerUnit: 0.01 },
  mm: { dimension: "length", canonicalPerUnit: 0.001 },
  rad: { dimension: "angle", canonicalPerUnit: 1 },
  deg: { dimension: "angle", canonicalPerUnit: PI / 180 },
  s: { dimension: "time", canonicalPerUnit: 1 },
  min: { dimension: "time", canonicalPerUnit: 60 },
  h: { dimension: "time", canonicalPerUnit: 3600 },
  d: { dimension: "time", canonicalPerUnit: 86400 },
  kg: { dimension: "mass", canonicalPerUnit: 1 },
  g: { dimension: "mass", canonicalPerUnit: 0.001 },
  t: { dimension: "mass", canonicalPerUnit: 1000 },
  "m/s": { dimension: "velocity", canonicalPerUnit: 1 },
  "km/s": { dimension: "velocity", canonicalPerUnit: 1000 },
  "km/h": { dimension: "velocity", canonicalPerUnit: 1000 / 3600 },
  "m/s2": { dimension: "acceleration", canonicalPerUnit: 1 },
  "1": { dimension: "dimensionless", canonicalPerUnit: 1 },
} as const satisfies Record<Unit, UnitSpec>;

const CANONICAL = {
  length: "m",
  angle: "rad",
  time: "s",
  mass: "kg",
  velocity: "m/s",
  acceleration: "m/s2",
  dimensionless: "1",
} as const satisfies { [D in Dimension]: UnitOf<D> };

/** Toutes les unités du vocabulaire, pour l'itération et les bancs. */
export const ALL_UNITS = Object.keys(SPECS) as readonly Unit[];

function specOf(unit: Unit): UnitSpec {
  if (!isUnit(unit)) throw new UnitError(coreFr.units.unknownUnit(String(unit)));
  return SPECS[unit];
}

export function isUnit(candidate: unknown): candidate is Unit {
  return typeof candidate === "string" && Object.hasOwn(SPECS, candidate);
}

export function dimensionOf(unit: Unit): Dimension {
  return specOf(unit).dimension;
}

/** L'unité SI canonique d'une dimension — celle du stockage interne. */
export function canonicalUnitFor<D extends Dimension>(dimension: D): UnitOf<D> {
  const unit = CANONICAL[dimension];
  if (!Object.hasOwn(CANONICAL, dimension)) {
    throw new UnitError(coreFr.units.unknownDimension(String(dimension)));
  }
  return unit;
}

/**
 * Construit une grandeur. Seul point d'entrée : il valide l'unité et rejette
 * les valeurs non finies, qui empoisonneraient silencieusement tout calcul en
 * aval.
 */
export function quantity<U extends Unit>(
  value: number,
  unit: U,
): Quantity<DimensionOfUnit<U>> {
  if (!isUnit(unit)) {
    throw new UnitError(coreFr.units.unknownUnit(String(unit)));
  }
  if (!Number.isFinite(value)) {
    throw new UnitError(coreFr.units.nonfiniteQuantity(String(unit), String(value)));
  }
  // `U` appartient à `UnitOf<DimensionOfUnit<U>>` par construction — c'est
  // exactement ainsi que `DimensionOfUnit` est défini — mais le compilateur ne
  // sait pas refermer cette boucle sur un paramètre générique. Assertion sûre,
  // et confinée à ce seul constructeur.
  return Object.freeze({ value, unit }) as unknown as Quantity<DimensionOfUnit<U>>;
}

/** Raccourci pour les grandeurs sans dimension (rapports, facteurs). */
export function dimensionless(value: number): Quantity<"dimensionless"> {
  return quantity(value, "1");
}

function assertSameDimension(a: Unit, b: Unit, operation: string): void {
  const da = dimensionOf(a);
  const db = dimensionOf(b);
  if (da !== db) {
    throw new UnitError(
      coreFr.units.incompatibleDimensions(operation, da, a, db, b),
    );
  }
}

/** Convertit vers une autre unité de la même dimension. */
export function convert<D extends Dimension>(
  q: Quantity<D>,
  target: Unit,
): Quantity<D> {
  if (!isUnit(target)) {
    throw new UnitError(coreFr.units.unknownUnit(String(target)));
  }
  assertSameDimension(q.unit, target, coreFr.units.conversion);
  if (!Number.isFinite(q.value)) throw new UnitError(coreFr.units.nonfiniteValue(String(q.value)));
  if (q.unit === target) return q;

  const canonicalValue = q.value * specOf(q.unit).canonicalPerUnit;
  return quantity(canonicalValue / specOf(target).canonicalPerUnit, target) as Quantity<D>;
}

/** Ramène à l'unité SI canonique de sa dimension. */
export function toCanonical<D extends Dimension>(q: Quantity<D>): Quantity<D> {
  return convert(q, canonicalUnitFor(dimensionOf(q.unit)));
}

/**
 * Extrait un nombre nu. **Unique porte de sortie documentée** du système
 * d'unités : à utiliser aux frontières — affichage, tracé, sérialisation — et
 * nulle part au milieu d'un calcul.
 */
export function valueIn(q: Quantity, unit: Unit): number {
  return convert(q, unit).value;
}

/** Somme, rendue en unité canonique. */
export function add<D extends Dimension>(
  a: Quantity<D>,
  b: NoInfer<Quantity<D>>,
): Quantity<D> {
  assertSameDimension(a.unit, b.unit, coreFr.units.addition);
  const canonical = canonicalUnitFor(dimensionOf(a.unit)) as UnitOf<D>;
  return quantity(convert(a, canonical).value + convert(b, canonical).value, canonical) as unknown as Quantity<D>;
}

/** Différence, rendue en unité canonique. */
export function subtract<D extends Dimension>(
  a: Quantity<D>,
  b: NoInfer<Quantity<D>>,
): Quantity<D> {
  assertSameDimension(a.unit, b.unit, coreFr.units.subtraction);
  const canonical = canonicalUnitFor(dimensionOf(a.unit)) as UnitOf<D>;
  return quantity(convert(a, canonical).value - convert(b, canonical).value, canonical) as unknown as Quantity<D>;
}

/** Multiplie par un facteur sans dimension. */
export function scale<D extends Dimension>(
  q: Quantity<D>,
  factor: Quantity<"dimensionless">,
): Quantity<D> {
  const canonical = canonicalUnitFor(dimensionOf(q.unit)) as UnitOf<D>;
  return quantity(convert(q, canonical).value * valueIn(factor, "1"), canonical) as unknown as Quantity<D>;
}

/**
 * Rapport de deux grandeurs de même dimension — le résultat est sans
 * dimension. C'est la façon propre d'obtenir un facteur sans jamais manipuler
 * de nombre nu.
 */
export function ratio<D extends Dimension>(
  a: Quantity<D>,
  b: NoInfer<Quantity<D>>,
): Quantity<"dimensionless"> {
  assertSameDimension(a.unit, b.unit, coreFr.units.ratio);
  const canonical = canonicalUnitFor(dimensionOf(a.unit)) as UnitOf<D>;
  const divisor = convert(b, canonical).value;
  if (divisor === 0) {
    throw new UnitError(coreFr.units.zeroRatioDenominator);
  }
  return dimensionless(convert(a, canonical).value / divisor);
}

// ── Ponts dimensionnels ──────────────────────────────────────────────────────
//
// La dynamique a besoin de franchir les dimensions : une vitesse fois un temps
// est une longueur. Plutôt qu'une algèbre dimensionnelle générale — lourde à
// écrire, lourde à lire, et dont ce projet n'a besoin que quatre fois — chaque
// passage porte un nom qui dit ce qu'il fait. Une opération manquante est alors
// une erreur de compilation, pas une conversion silencieuse.

/** `v · dt` — le déplacement parcouru à vitesse constante. */
export function displacement(
  velocity: Quantity<"velocity">,
  duration: Quantity<"time">,
): Quantity<"length"> {
  return quantity(
    convert(velocity, "m/s").value * convert(duration, "s").value,
    "m",
  );
}

/** `a · dt` — la variation de vitesse sous accélération constante. */
export function velocityChange(
  acceleration: Quantity<"acceleration">,
  duration: Quantity<"time">,
): Quantity<"velocity"> {
  return quantity(
    convert(acceleration, "m/s2").value * convert(duration, "s").value,
    "m/s",
  );
}

/** `x / t` — la vitesse moyenne sur un intervalle. */
export function velocityOver(
  length: Quantity<"length">,
  duration: Quantity<"time">,
): Quantity<"velocity"> {
  const seconds = convert(duration, "s").value;
  if (seconds === 0) {
    throw new UnitError(coreFr.units.zeroDuration);
  }
  return quantity(convert(length, "m").value / seconds, "m/s");
}
